import { appConfig } from '../config/env'
import type { JourneyRead, MessageActivity, NextAction, TurnFailure } from './resources/models'
import type { Citation } from './resources/citationTypes'
import {
  conversationCitations,
  parseConversationCitations,
} from './resources/citationValidation'
import type { ApiFailure, ApiResult, ProblemDetails, ValidationIssue } from './types'
import { getSessionClient, refreshRequired } from '../auth/sessionClient'
import { ApiError } from './types'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
export type QueryScalar = string | number | boolean | null | undefined
export type QueryValue = QueryScalar | readonly (string | number)[]
export type QueryParams = Readonly<Record<string, QueryValue>>

export interface RequestOptions {
  readonly path: string
  readonly method?: HttpMethod | undefined
  readonly query?: QueryParams | undefined
  readonly body?: unknown
  readonly rawBody?: Blob | ArrayBuffer | undefined
  readonly responseType?: 'json' | 'blob' | undefined
  readonly ifMatch?: string | undefined
  readonly signal?: AbortSignal | undefined
  readonly headers?: Readonly<Record<string, string>> | undefined
}

export type ChatEvent =
  | { readonly event: 'token'; readonly text: string; readonly message_id: string }
  | { readonly event: 'start'; readonly message_id: string }
  | {
      readonly event: 'activity'
      readonly message_id: string
      readonly activity: MessageActivity
    }
  | {
      readonly event: 'stopped'
      readonly message_id: string
      readonly content: string
      readonly journey: JourneyRead | null
      readonly citations?: readonly Citation[]
    }
  | {
      readonly event: 'done'
      readonly message_id: string
      readonly journey: JourneyRead
      readonly citations?: readonly Citation[]
    }
  | {
      readonly event: 'error'
      readonly detail: string
      readonly message_id: string
      readonly failure: TurnFailure
      readonly journey: JourneyRead | null
      readonly citations?: readonly Citation[]
    }

export type ChatTerminal = Extract<ChatEvent, { event: 'done' | 'error' | 'stopped' }>
export const REQUEST_ID_HEADER = 'x-request-id'
let baseUrlOverride: string | null = null
const changedSession: ApiFailure = {
  kind: 'Unauthenticated',
  status: 401,
  problem: null,
  requestId: null,
  retryable: false,
  message: 'The account changed during this request.',
}

export function setApiBaseUrl(url: string | null): void {
  baseUrlOverride = url === null ? null : url.replace(/\/+$/, '')
}

export function buildUrl(path: string, query?: QueryParams): string {
  const base = baseUrlOverride ?? (appConfig.ok ? appConfig.config.apiBaseUrl : '')
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null) continue
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item))
    } else search.append(key, String(value))
  }
  const suffix = path.startsWith('/') ? path : `/${path}`
  return `${base}${suffix}${search.size ? `?${search.toString()}` : ''}`
}

interface FailurePayload {
  readonly detail?: unknown
  readonly title?: unknown
  readonly type?: unknown
}

interface ConflictPayload {
  readonly code?: unknown
  readonly current_revision?: unknown
  readonly sections?: unknown
}

interface ValidationPayload {
  readonly loc?: unknown
  readonly msg?: unknown
  readonly type?: unknown
}

interface StreamPayload {
  readonly text?: unknown
  readonly message_id?: unknown
  readonly detail?: unknown
  readonly journey?: JourneyRead | null
  readonly failure?: TurnFailure
  readonly content?: unknown
  readonly activity?: unknown
  readonly citations?: unknown
}

export function isRetryableStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500
}

function toFailure(response: Response, body: unknown): ApiFailure {
  const status = response.status
  const record =
    typeof body === 'object' && body !== null && !Array.isArray(body)
      ? (body as FailurePayload)
      : null
  const detail: unknown = record?.detail ?? body
  const problem: ProblemDetails | null =
    record &&
    (response.headers.get('content-type')?.includes('application/problem+json') ||
      typeof record.title === 'string')
      ? {
          ...record,
          type: typeof record.type === 'string' ? record.type : 'about:blank',
          title: typeof record.title === 'string' ? record.title : `HTTP ${status}`,
          status,
          detail: typeof detail === 'string' ? detail : undefined,
        }
      : null
  const context = {
    status,
    problem,
    detail,
    requestId: response.headers.get(REQUEST_ID_HEADER),
    retryable: isRetryableStatus(status),
    message:
      typeof detail === 'string' && detail.trim()
        ? detail
        : (problem?.title ?? `Request failed with status ${status}.`),
  }
  if (status === 401) return { ...context, kind: 'Unauthenticated' }
  if (status === 403) return { ...context, kind: 'Forbidden' }
  if (status === 404) return { ...context, kind: 'NotFound' }
  if (status === 409) {
    const conflict =
      typeof detail === 'object' && detail !== null && !Array.isArray(detail)
        ? (detail as ConflictPayload)
        : null
    return {
      ...context,
      kind: 'Conflict',
      code: typeof conflict?.code === 'string' ? conflict.code : null,
      currentRevision:
        typeof conflict?.current_revision === 'number' ? conflict.current_revision : null,
      sections: Array.isArray(conflict?.sections)
        ? conflict.sections.filter((item): item is string => typeof item === 'string')
        : [],
    }
  }
  if (status === 412) return { ...context, kind: 'StaleWrite', retryable: false }
  if (status === 400 || status === 422 || status === 428) {
    const issues: ValidationIssue[] = []
    if (Array.isArray(detail)) {
      for (const issue of detail) {
        if (typeof issue !== 'object' || issue === null || Array.isArray(issue)) continue
        const validation = issue as ValidationPayload
        issues.push({
          pointer: Array.isArray(validation.loc) ? validation.loc.join('.') : '',
          message: typeof validation.msg === 'string' ? validation.msg : 'Invalid value.',
          code: typeof validation.type === 'string' ? validation.type : 'invalid',
        })
      }
    }
    return { ...context, kind: 'Validation', issues }
  }
  return { ...context, kind: 'Unexpected' }
}

function transportFailure(error: unknown, signal?: AbortSignal): ApiFailure {
  const cancelled = signal?.aborted || (error instanceof Error && error.name === 'AbortError')
  return {
    kind: 'Network',
    status: 0,
    problem: null,
    requestId: null,
    retryable: !cancelled,
    message: cancelled
      ? 'The request was cancelled.'
      : 'The request could not reach the Argus API.',
  }
}

async function open(options: RequestOptions, accept: string): Promise<ApiResult<Response>> {
  const headers = new Headers({ Accept: accept, ...options.headers })
  const method = options.method ?? 'GET'
  if (options.ifMatch !== undefined) {
    if (!/^"[!#-~]+"$/.test(options.ifMatch)) {
      return {
        ok: false,
        failure: {
          kind: 'Validation',
          status: 0,
          problem: null,
          requestId: null,
          retryable: false,
          message: 'Use one strong quoted ETag from the publication preview.',
          issues: [],
        },
      }
    }
    headers.set('If-Match', options.ifMatch)
  }
  const session = getSessionClient()
  const generation = session.getGeneration()
  try {
    await session.ensure()
  } catch (error) {
    return {
      ok: false,
      failure:
        error instanceof ApiError ? error.failure : transportFailure(error, options.signal),
    }
  }
  if (method !== 'GET') headers.set('X-Argus-CSRF', '1')
  const init: RequestInit = { method, headers, credentials: 'include' }
  if (options.signal) init.signal = options.signal
  if (options.rawBody !== undefined) {
    init.body = options.rawBody
    headers.set('Content-Type', 'application/octet-stream')
  } else if (options.body !== undefined) {
    init.body = JSON.stringify(options.body)
    headers.set('Content-Type', 'application/json')
  }
  try {
    let response = await fetch(buildUrl(options.path, options.query), init)
    if (
      response.status === 401 &&
      refreshRequired(
        await response
          .clone()
          .json()
          .catch(() => null),
      )
    ) {
      await session.renew()
      if (generation !== session.getGeneration()) return { ok: false, failure: changedSession }
      response = await fetch(buildUrl(options.path, options.query), init)
    }
    if (generation !== session.getGeneration()) return { ok: false, failure: changedSession }
    if (!response.ok) {
      const text = await response.text()
      let body: unknown = text
      try {
        body = JSON.parse(text) as unknown
      } catch {
        /* Non-JSON errors retain the response text. */
      }
      return { ok: false, failure: toFailure(response, body) }
    }
    return {
      ok: true,
      data: response,
      status: response.status,
      etag: response.headers.get('etag'),
      requestId: response.headers.get(REQUEST_ID_HEADER),
    }
  } catch (error) {
    return {
      ok: false,
      failure:
        error instanceof ApiError ? error.failure : transportFailure(error, options.signal),
    }
  }
}

export async function request<T>(options: RequestOptions): Promise<ApiResult<T>> {
  const session = getSessionClient()
  const generation = session.getGeneration()
  const result = await open(
    options,
    options.responseType === 'blob'
      ? 'application/octet-stream'
      : 'application/json, application/problem+json',
  )
  if (!result.ok) return result
  try {
    const data: unknown =
      result.status === 204 || result.status === 205
        ? undefined
        : options.responseType === 'blob'
          ? await result.data.blob()
          : await result.data.json()
    if (generation !== session.getGeneration()) return { ok: false, failure: changedSession }
    return { ...result, data: data as T }
  } catch (error) {
    const failure = transportFailure(error, options.signal)
    if (!failure.retryable) return { ok: false, failure }
    return {
      ok: false,
      failure: {
        ...failure,
        kind: 'Unexpected',
        status: result.status,
        requestId: result.requestId,
        retryable: options.method !== undefined && options.method !== 'GET',
        message: 'The API response could not be read.',
      },
    }
  }
}

function isNextAction(action: NextAction | null | undefined): action is NextAction {
  return (
    typeof action === 'object' &&
    action !== null &&
    typeof action.conversation_id === 'string' &&
    [
      'choose_initiative',
      'add_context',
      'develop_solution',
      'complete_draft',
      'check_readiness',
      'retry_readiness',
      'submit_prd',
      'review_submission',
      'revise_draft',
      'view_decision',
      'retry_turn',
    ].includes(action.kind) &&
    (action.initiative_id === undefined ||
      action.initiative_id === null ||
      typeof action.initiative_id === 'string') &&
    (action.target_id === undefined ||
      action.target_id === null ||
      typeof action.target_id === 'string') &&
    (action.expected_revision === undefined ||
      action.expected_revision === null ||
      typeof action.expected_revision === 'number')
  )
}

function decodeEvent(event: string, text: string): ChatEvent {
  const parsed: unknown = JSON.parse(text)
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))
    throw new Error('Invalid stream event')
  const value = parsed as StreamPayload
  if (
    event === 'token' &&
    typeof value.text === 'string' &&
    typeof value.message_id === 'string'
  )
    return { event, text: value.text, message_id: value.message_id }
  if (event === 'start' && typeof value.message_id === 'string')
    return { event, message_id: value.message_id }
  if (event === 'activity' && typeof value.message_id === 'string') {
    const activity = value.activity
    if (typeof activity === 'object' && activity !== null && !Array.isArray(activity)) {
      const record = activity as Record<string, unknown>
      if (
        typeof record.id === 'string' &&
        typeof record.name === 'string' &&
        typeof record.label === 'string' &&
        ['running', 'complete', 'failed', 'stopped'].includes(String(record.status)) &&
        typeof record.started_at === 'string' &&
        Number.isFinite(Date.parse(record.started_at)) &&
        (record.finished_at === null ||
          (typeof record.finished_at === 'string' &&
            Number.isFinite(Date.parse(record.finished_at))))
      )
        return {
          event,
          message_id: value.message_id,
          activity: {
            id: record.id,
            name: record.name,
            label: record.label,
            status: record.status as MessageActivity['status'],
            started_at: record.started_at,
            finished_at: record.finished_at,
          },
        }
    }
    throw new Error('Invalid activity event')
  }
  const journey = value.journey
  const validJourney =
    journey !== null &&
    typeof journey === 'object' &&
    typeof journey.workspace_id === 'string' &&
    (journey.initiative_id === null || typeof journey.initiative_id === 'string') &&
    (journey.draft_revision === null || typeof journey.draft_revision === 'number') &&
    (journey.state === null ||
      ['Gather', 'Sharpen', 'Propose', 'Decide', 'Deliver'].includes(journey.state)) &&
    (journey.next_action === null || isNextAction(journey.next_action))
  if (event === 'done' && typeof value.message_id === 'string' && validJourney && journey) {
    return {
      event,
      message_id: value.message_id,
      journey,
      citations: decodeCitations(value.citations),
    }
  }
  if (
    event === 'stopped' &&
    typeof value.message_id === 'string' &&
    typeof value.content === 'string' &&
    (journey === null || validJourney) &&
    journey !== undefined
  )
    return {
      event,
      message_id: value.message_id,
      content: value.content,
      journey,
      citations: decodeCitations(value.citations),
    }
  const failure = value.failure
  if (
    event === 'error' &&
    typeof value.detail === 'string' &&
    typeof value.message_id === 'string' &&
    typeof failure === 'object' &&
    failure !== null &&
    typeof failure.detail === 'string' &&
    [
      'model_failed',
      'stale_revision',
      'draft_edit_failed',
      'access_denied',
      'interrupted',
      'turn_superseded',
      'citation_invalid',
    ].includes(failure.code) &&
    (failure.current_revision === undefined ||
      failure.current_revision === null ||
      typeof failure.current_revision === 'number') &&
    (failure.retry_action === undefined ||
      failure.retry_action === null ||
      isNextAction(failure.retry_action)) &&
    (journey === null || validJourney) &&
    journey !== undefined
  ) {
    return {
      event,
      detail: value.detail,
      message_id: value.message_id,
      failure,
      journey,
      citations: decodeCitations(value.citations, failure.code === 'citation_invalid'),
    }
  }
  throw new Error('Invalid stream event')
}

function decodeCitations(value: unknown, invalid = false): readonly Citation[] {
  try {
    return parseConversationCitations(value, invalid)
  } catch {
    throw new Error('Invalid stream event')
  }
}

export async function streamRequest(
  options: RequestOptions,
  onEvent: (event: ChatEvent) => void,
): Promise<ApiResult<ChatTerminal>> {
  const session = getSessionClient()
  const generation = session.getGeneration()
  const result = await open(options, 'text/event-stream')
  if (!result.ok) return result
  if (
    !result.data.body ||
    !result.data.headers.get('content-type')?.includes('text/event-stream')
  ) {
    return {
      ok: false,
      failure: {
        kind: 'Unexpected',
        status: result.status,
        problem: null,
        requestId: result.requestId,
        retryable: false,
        message: 'The API did not return an event stream.',
      },
    }
  }
  const reader = result.data.body.getReader()
  const unsubscribe = session.subscribe(() => {
    if (generation !== session.getGeneration()) void reader.cancel().catch(() => undefined)
  })
  const decoder = new TextDecoder()
  let pending = ''
  let eventName = 'message'
  let data: string[] = []
  let terminal: ChatTerminal | null = null
  const messageText = new Map<string, string>()
  let callbackFailed = false
  const dispatch = () => {
    if (generation !== session.getGeneration()) throw new ApiError(changedSession)
    if (
      data.length &&
      ['start', 'activity', 'token', 'done', 'error', 'stopped'].includes(eventName)
    ) {
      const event = decodeEvent(eventName, data.join('\n'))
      if (event.event === 'token') {
        messageText.set(
          event.message_id,
          (messageText.get(event.message_id) ?? '') + event.text,
        )
      } else if (
        event.event === 'done' ||
        event.event === 'error' ||
        event.event === 'stopped'
      ) {
        try {
          conversationCitations(
            event.citations,
            event.event === 'stopped'
              ? event.content
              : (messageText.get(event.message_id) ?? ''),
            event.event === 'error' && event.failure.code === 'citation_invalid',
          )
        } catch {
          throw new Error('Invalid stream event')
        }
      }
      try {
        onEvent(event)
      } catch (error) {
        callbackFailed = true
        throw error
      }
      if (event.event === 'done' || event.event === 'error' || event.event === 'stopped')
        terminal = event
    }
    data = []
    eventName = 'message'
  }
  try {
    while (!terminal) {
      const chunk = await reader.read()
      if (generation !== session.getGeneration()) return { ok: false, failure: changedSession }
      pending += decoder.decode(chunk.value, { stream: !chunk.done })
      let end = pending.search(/[\r\n]/)
      while (end !== -1) {
        if (!chunk.done && pending[end] === '\r' && end === pending.length - 1) break
        const line = pending.slice(0, end)
        const width = pending[end] === '\r' && pending[end + 1] === '\n' ? 2 : 1
        pending = pending.slice(end + width)
        if (!line) dispatch()
        else if (!line.startsWith(':')) {
          const colon = line.indexOf(':')
          const field = colon === -1 ? line : line.slice(0, colon)
          let value = colon === -1 ? '' : line.slice(colon + 1)
          if (value.startsWith(' ')) value = value.slice(1)
          if (field === 'event') eventName = value
          if (field === 'data') data.push(value)
        }
        if (terminal) break
        end = pending.search(/[\r\n]/)
      }
      if (chunk.done) break
    }
    if (terminal) return { ...result, data: terminal }
    return {
      ok: false,
      failure: {
        kind: 'Network',
        status: result.status,
        problem: null,
        requestId: result.requestId,
        retryable: false,
        message: 'The response was interrupted; read message history before an explicit retry.',
      },
    }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, failure: error.failure }
    const malformed =
      error instanceof SyntaxError ||
      (error instanceof Error && error.message === 'Invalid stream event')
    return {
      ok: false,
      failure: {
        ...transportFailure(error, options.signal),
        kind: malformed || callbackFailed ? 'Unexpected' : 'Network',
        status: result.status,
        requestId: result.requestId,
        retryable: false,
        message: callbackFailed
          ? 'The stream callback failed.'
          : malformed
            ? 'The API returned an invalid stream event.'
            : options.signal?.aborted
              ? 'The request was cancelled.'
              : 'The response was interrupted; read message history before an explicit retry.',
      },
    }
  } finally {
    unsubscribe()
    void reader.cancel().catch(() => undefined)
    reader.releaseLock()
  }
}
